import React from "react";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import Button from "../Button";
import MeshField from "./MeshField";
import Doodle from "./Doodle";
import Highlighter from "./Highlighter";
// Imported, not referenced from public/, so Vite content-hashes the filename.
// A recut that keeps its URL is served stale by every cache that already has
// it: on 2026-07-31 a phone showed the new photo on one side and the previous
// one on the other, from the same deploy.
import groupA from "../../media/story/hero-group-a.webp";
import groupASm from "../../media/story/hero-group-a-sm.webp";
import groupB from "../../media/story/hero-group-b.webp";
import groupBSm from "../../media/story/hero-group-b-sm.webp";

// Act 1 of the story site: the invitation.
//
// The mesh IS the hero. No stock photograph behind the headline, because the
// photography earns more later (Act 5) when it is not competing with type, and
// because the brand's signature surface has never appeared on the website.
//
// Copy is possibility-led, not need-led. Loneliness framing is press-only by
// standing brand rule, so nothing here names a problem; it opens a door.
//
// MOTION: deliberately NOT framer-motion. Its `initial` prop is written as an
// inline style, and this site is prerendered, so `initial={{opacity:0}}` ships
// opacity:0 in the static HTML and the headline cannot paint until React
// hydrates. The rest of the site does this today (36 such elements in
// dist/index.html, including the live home page H1). `.story-enter` is a pure
// CSS animation that runs at first paint and degrades to plain visible text
// when JS is off. See components/story/Reveal.tsx for the full reasoning.
//
// TONE, 2026-07-31. The founders read this section as "too childish" while
// naming Act 2 (MeshMoment) as the benchmark for tasteful weight and colour.
// Both set Fraunces at 700, so the weight was never the difference; the SIZE
// was. This ran at clamp(2.9rem, 10vw, 6.5rem) against Act 2's
// clamp(2.1rem, 6.2vw, 4rem), and Fraunces is a high-contrast display serif
// whose thick stems go blobby as it scales up. Three changes, all borrowed
// from the section they approved rather than invented:
//
//   1. the headline drops to clamp(2.5rem, 7.2vw, 5rem), still the largest
//      type on the page but near enough to Act 2 to read as the same voice
//   2. the eyebrow was a handwritten Caveat line at text-3xl/4xl, which is the
//      single most playful element on the page. Act 2 opens with an 11px
//      uppercase micro-label, so this now does too
//   3. six scattered doodles drop to four, smaller and fainter
//
// The font file is deliberately NOT touched. It is instanced at SOFT=60 and
// WONK=1 (Fraunces' "wonky" alternates), which would be the obvious suspect
// for childishness, but Act 2 uses that same instance and is the benchmark, so
// re-instancing would break the thing they said was right.

const REASSURANCE = [
  "Free to join",
  "Real people, real names",
  "London, and growing",
];

const StoryHero: React.FC = () => (
  // max-sm:pb-44 reserves a clear band at the bottom of a phone screen for the
  // cut-out group. Without it the flex centring puts the reassurance list and
  // the scroll cue straight on top of the photograph, and both become
  // unreadable against the faces.
  <section className="relative w-full min-h-[92vh] flex flex-col justify-center items-center overflow-hidden px-6 pt-32 pb-16 max-sm:pb-60">
    <MeshField variant="hero" />

    {/* Scattered marks. Mobile positions are set EXPLICITLY rather than
        inherited: the desktop placements ring a wide headline, and on a 390px
        screen that same ring lands on top of it, which is why these were
        originally hidden below md. Under md they move to the top and bottom
        edges and scale down, so they frame the type instead of sitting in it. */}
    <div aria-hidden="true" className="absolute inset-0">
      <Doodle
        name="star"
        size={38}
        rotate={-12}
        className="absolute w-6 h-6 md:w-auto md:h-auto left-[9%] top-[24%] max-md:left-[4%] max-md:top-[6%] text-salmon-dark/50 story-float"
      />
      <Doodle
        name="squiggle"
        size={62}
        rotate={6}
        className="absolute w-7 h-7 md:w-auto md:h-auto right-[11%] top-[19%] max-md:right-[4%] max-md:top-[4%] text-teal/35 story-float"
        style={{ animationDelay: "-2.4s" }}
      />
      <Doodle
        name="loop"
        size={46}
        rotate={-8}
        className="absolute w-6 h-6 md:w-auto md:h-auto right-[15%] bottom-[34%] max-md:right-[5%] max-md:bottom-[16%] text-ink/20 story-float"
        style={{ animationDelay: "-3.1s" }}
      />
      <Doodle
        name="starFilled"
        size={18}
        className="absolute w-3 h-3 md:w-auto md:h-auto left-[30%] top-[13%] max-md:left-[48%] max-md:top-[1%] text-salmon/45"
      />
    </div>

    {/* The cut-out groups (LL-0199). Same sticker treatment as Act 2: a
        segmented GROUP with a soft drop shadow, sitting on the mesh rather than
        inside a frame. Groups, not individuals: an earlier pass cut single
        figures and read as a stock jogger rather than as friends.
        See scripts/make-hero-groups.py for the sources and the two dead ends.

        Each sits flush to its own side, because each photo is full-bleed and so
        has a straight edge wherever a person runs off frame. Those edges are
        pointed at the VIEWPORT edge where they cannot be seen: street-night
        runs off the bottom and the left, park-dusk only touches the right.
        Nothing is faded. The previous pair needed an alpha ramp on the inward
        edge to hide a slice through a person; these do not, because the
        inward-facing side of each group is a whole person, and the script
        refuses to build them if that stops being true.

        Both show on a phone. The earlier pair could not: they were five- and
        six-person groups, so at phone scale the faces came out around 20px and
        read as texture. These are four and three people, so the same width
        carries a face you can actually read. */}
    <div
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 z-0 pointer-events-none"
    >
      <img
        src={groupA}
        srcSet={`${groupASm} 691w, ${groupA} 1382w`}
        sizes="(max-width: 640px) 44vw, (max-width: 1024px) 30vw, 30vw"
        alt=""
        width={1382}
        height={1689}
        className="absolute bottom-0 left-0 w-[44%] sm:w-[32%] md:w-[30%] lg:w-[30%] max-w-[390px] h-auto drop-shadow-[0_26px_40px_rgba(45,56,73,0.16)]"
      />
      <img
        src={groupB}
        srcSet={`${groupBSm} 655w, ${groupB} 1310w`}
        sizes="(max-width: 640px) 38vw, (max-width: 1024px) 26vw, 27vw"
        alt=""
        width={1310}
        height={1899}
        className="absolute bottom-0 right-0 w-[38%] sm:w-[28%] md:w-[26%] lg:w-[27%] max-w-[340px] h-auto drop-shadow-[0_26px_40px_rgba(45,56,73,0.16)]"
      />
    </div>

    <div className="relative z-10 max-w-4xl mx-auto text-center pt-4">
      <p
        className="story-enter text-[11px] font-bold uppercase tracking-[0.18em] text-salmon-dark mb-5"
        style={{ "--enter-delay": "100ms" } as React.CSSProperties}
      >
        Possibilities, in the moment
      </p>

      <h1
        className="story-enter font-headline text-ink tracking-[-0.025em] leading-[1.08] text-[clamp(2.5rem,7.2vw,5rem)] text-balance"
        // Fraunces at 700, matching Act 2. Tracking is looser than the old sans
        // setting (-0.025em vs -0.04em) because a high-contrast serif closes up
        // faster than a geometric sans at display size and starts colliding.
        style={
          { fontWeight: 700, "--enter-delay": "170ms" } as React.CSSProperties
        }
      >
        Find the people
        <br />
        you <Highlighter tone="coral">mesh</Highlighter> with.
      </h1>

      {/* One line, not three. The cut sentence ("The plan comes first and the
          group chat follows...") is not lost: it is the headline of Act 3,
          where it has room to be the point rather than a qualifier. */}
      <p
        className="story-enter mt-6 text-lg md:text-xl text-ink/70 leading-relaxed max-w-lg mx-auto"
        style={{ "--enter-delay": "300ms" } as React.CSSProperties}
      >
        Post a plan, or turn up to one. A coffee, a walk, a 6:30 kickabout.
      </p>

      <div
        className="story-enter mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        style={{ "--enter-delay": "420ms" } as React.CSSProperties}
      >
        <Link to="/download">
          <Button variant="primary" size="lg">
            Get the app
          </Button>
        </Link>
        <Link to="/how-it-works">
          <Button variant="outline" size="lg">
            See how it works
          </Button>
        </Link>
      </div>

      <ul
        // max-w keeps this clear of the cut-out figures in the bottom corners;
        // unconstrained it ran edge to edge on a phone and its second line
        // crossed straight over one of them.
        className="story-enter mt-9 mx-auto max-w-[17rem] sm:max-w-[26rem] lg:max-w-none flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm font-semibold text-ink/60"
        style={{ "--enter-delay": "560ms" } as React.CSSProperties}
      >
        {REASSURANCE.map((item, i) => (
          <li key={item} className="flex items-center gap-3">
            {i > 0 && (
              <span aria-hidden="true" className="text-salmon">
                &middot;
              </span>
            )}
            {item}
          </li>
        ))}
      </ul>
    </div>

    <a
      href="#mesh"
      className="story-enter relative mt-14 hidden sm:flex flex-col items-center gap-2 text-ink/50 hover:text-salmon-dark transition-colors"
      style={{ "--enter-delay": "800ms" } as React.CSSProperties}
      aria-label="Scroll to see why it works"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.18em]">
        Scroll
      </span>
      <ArrowDown size={16} className="story-bob" />
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .story-bob { animation: story-bob 2.2s ease-in-out infinite; }
          .story-float { animation: story-float 7s ease-in-out infinite; }
        }
        /* Uses the independent "translate" property, NOT transform. Doodle sets
           its rotation via an inline transform, so animating transform here
           would silently override it and flatten every rotated mark. */
        @keyframes story-float {
          0%, 100% { translate: 0 0; }
          50% { translate: 0 -9px; }
        }
        @keyframes story-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(5px); }
        }
      `}</style>
    </a>
  </section>
);

export default StoryHero;
